import  { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Rating,
  Tabs,
  Tab,
  Divider,
} from "@mui/material";
import {
  Person as PersonIcon,
  Work as WorkIcon,
  Assignment as AssignmentIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import Layout from "../components/layout/Calayout";

const CAReviewPage = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  const [rating, setRating] = useState(0);

  // Sample CA Profile Data
  const caProfile = {
    name: "Rajesh Kumar Sharma",
    designation: "Senior Chartered Accountant",
    specializations: ["Tax Consulting", "Audit", "Financial Planning"],
    experience: 12,
    totalClients: 150,
    successRate: 95,
    languages: ["English", "Hindi", "Marathi"],
    contact: {
      email: "rajesh.sharma@caservices.com",
      phone: "+91 98765 43210",
    },
  };

  // Sample Reviews Data
  const reviews = [
    {
      id: 1,
      clientName: "Amit Patel",
      date: "2024-02-15",
      rating: 4.5,
      service: "Tax Filing",
      comment:
        "Excellent professional approach and timely completion of my tax filing.",
    },
    {
      id: 2,
      clientName: "Priya Sharma",
      date: "2024-01-22",
      rating: 5,
      service: "Financial Consulting",
      comment: "Comprehensive guidance and very detailed financial advice.",
    },
  ];

  // Sample Services
  const services = [
    {
      id: 1,
      name: "Tax Filing",
      description: "Comprehensive tax filing and optimization services",
      price: "₹5,000 - ₹15,000",
    },
    {
      id: 2,
      name: "Audit Services",
      description: "Detailed financial audit and compliance checking",
      price: "₹10,000 - ₹50,000",
    },
  ];

  const renderProfileSection = () => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <Avatar
              sx={{
                width: 120,
                height: 120,
                margin: "auto",
              }}
              src="/path/to/profile/image.jpg"
            >
              {caProfile.name.charAt(0)}
            </Avatar>
          </Grid>
          <Grid item xs={12} md={9}>
            <Typography variant="h5">{caProfile.name}</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {caProfile.designation}
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
              {caProfile.specializations.map((spec) => (
                <Chip
                  key={spec}
                  label={spec}
                  icon={<WorkIcon />}
                  variant="outlined"
                />
              ))}
            </Box>

            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={6} md={3}>
                <Typography variant="body2">
                  <strong>Experience:</strong> {caProfile.experience} Years
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2">
                  <strong>Clients:</strong> {caProfile.totalClients}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2">
                  <strong>Success Rate:</strong> {caProfile.successRate}%
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderReviewSection = () => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6">Client Reviews</Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenReviewDialog(true)}
          >
            Write a Review
          </Button>
        </Box>

        {reviews.map((review) => (
          <Box
            key={review.id}
            sx={{
              mb: 2,
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Box display="flex" justifyContent="space-between">
              <Typography variant="subtitle1">{review.clientName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {review.date}
              </Typography>
            </Box>
            <Rating value={review.rating} readOnly precision={0.5} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              {review.comment}
            </Typography>
            <Chip
              label={review.service}
              size="small"
              variant="outlined"
              sx={{ mt: 1 }}
            />
          </Box>
        ))}
      </CardContent>
    </Card>
  );

  const renderServicesSection = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Services
        </Typography>
        {services.map((service) => (
          <Box key={service.id} sx={{ mb: 2 }}>
            <Typography variant="subtitle1">{service.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {service.description}
            </Typography>
            <Typography variant="body2" color="primary">
              Price Range: {service.price}
            </Typography>
            <Divider sx={{ my: 1 }} />
          </Box>
        ))}
      </CardContent>
    </Card>
  );

  const renderReviewDialog = () => (
    <Dialog open={openReviewDialog} onClose={() => setOpenReviewDialog(false)}>
      <DialogTitle>Write a Review</DialogTitle>
      <DialogContent>
        <Rating
          value={rating}
          onChange={(event, newValue) => setRating(newValue)}
          sx={{ mb: 2 }}
        />
        <TextField
          autoFocus
          margin="dense"
          label="Your Review"
          fullWidth
          variant="outlined"
          multiline
          rows={4}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenReviewDialog(false)}>Cancel</Button>
        <Button color="primary" variant="contained">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Layout>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom>
          CA Profile & Reviews
        </Typography>

        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}
          sx={{ mb: 2 }}
        >
          <Tab label="Profile" icon={<PersonIcon />} />
          <Tab label="Reviews" icon={<StarIcon />} />
          <Tab label="Services" icon={<AssignmentIcon />} />
        </Tabs>

        {selectedTab === 0 && renderProfileSection()}
        {selectedTab === 1 && renderReviewSection()}
        {selectedTab === 2 && renderServicesSection()}

        {renderReviewDialog()}
      </Box>
    </Layout>
  );
};

export default CAReviewPage;
